import {
  BrainIcon,
  ClipboardListIcon,
  SearchIcon,
  Table2Icon,
  TargetIcon,
  FileTextIcon,
  TrendingUpIcon,
  ShuffleIcon,
  TimerIcon,
  HelpCircleIcon,
  PuzzleIcon,
  ListChecksIcon,
  MessageCircleQuestionIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export const CHECKOUT_URL = "https://sensory-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/cxvWdMJBVBR7KR5CBkJVmd/SENSORY-EN.png",
  creator: "https://cdn.magicpatterns.com/uploads/cS72gNnuhNJ6sf7wAJm2mm/DR._ELOWEN_VARELLI_-_SENSORY.png",
};

export const pathwaySteps = [
  "Observation",
  "Sensory System",
  "Pattern",
  "Missing Information",
  "Working Hypothesis",
  "Functional Goal",
  "Next Clinical Step",
];

export const beforeItems = [
  "You collect dozens of observations but struggle to connect them.",
  "You jump between notes, books and courses trying to remember what matters.",
  "You aren't always sure what information is still missing.",
  "You know the theory but struggle to translate it into a practical reasoning process.",
  "Complex cases make you feel like you're starting from zero every time.",
  "You sometimes finish an evaluation with more questions than answers.",
];

export const afterItems = [
  "You organize the case through a repeatable visual pathway.",
  "You identify which information deserves further investigation.",
  "You connect functional observations with sensory systems more systematically.",
  "You separate observations from assumptions.",
  "You create clearer working hypotheses before deciding on the next step.",
  "You have a practical reference you can return to whenever a case feels complicated.",
];

export type Situation = { icon: LucideIcon; text: string };

export const situations: Situation[] = [
  {
    icon: ShuffleIcon,
    text: "You have a child with several sensory and functional complaints, but you're not sure where to begin organizing the case.",
  },
  {
    icon: TimerIcon,
    text: "You spend too much time going back through notes, books and course materials before making sense of your observations.",
  },
  {
    icon: HelpCircleIcon,
    text: "You understand sensory modulation, discrimination and praxis — but connecting those concepts to a real case still feels difficult.",
  },
  {
    icon: PuzzleIcon,
    text: "You have many observations, but you aren't always sure which ones deserve more investigation.",
  },
  {
    icon: ListChecksIcon,
    text: "You want a more organized way to move from assessment information to functional goals.",
  },
  {
    icon: MessageCircleQuestionIcon,
    text: "You leave a session thinking: “I know something is happening… I just need a better way to organize my reasoning.”",
  },
];

export type Volume = {
  number: string;
  title: string;
  intro: string;
  listLabel: string;
  items: string[];
  goal: string;
  color: string;
  tint: string;
};

export const volumes: Volume[] = [
  {
    number: "Volume I",
    title: "Start With the Right Question",
    intro:
      "Move beyond the initial complaint and organize the functional concern before jumping into sensory interpretations.",
    listLabel: "You'll learn how to structure:",
    items: [
      "The family's main concern",
      "Functional impact",
      "Context",
      "Activities affected",
      "Participation",
      "Relevant history",
      "Initial observations",
    ],
    goal: "Know what problem you're actually trying to understand before searching for the answer.",
    color: "#E0607E",
    tint: "#FDEEF2",
  },
  {
    number: "Volume II",
    title: "The Sensory Systems Decision Map",
    intro:
      "Stop memorizing disconnected sensory concepts. Use a visual map to organize observations around the major sensory systems.",
    listLabel: "You'll receive:",
    items: [
      "Sensory system maps",
      "Functional correlations",
      "Observation prompts",
      "Comparison tables",
      "Key questions",
      "Quick-reference charts",
    ],
    goal: "Ask “What else do I need to understand before interpreting this observation?”",
    color: "#3F86D6",
    tint: "#ECF4FD",
  },
  {
    number: "Volume III",
    title: "Modulation, Discrimination & Praxis — Organized",
    intro: "Three areas that can become confusing very quickly — turned into visual reasoning pathways.",
    listLabel: "You'll explore:",
    items: [
      "Modulation",
      "Sensory discrimination",
      "Praxis",
      "Common functional manifestations",
      "Observation patterns",
      "Questions to investigate",
      "Comparison matrices",
      "Reasoning prompts",
    ],
    goal: "Make complex concepts easier to navigate when you're looking at a real case.",
    color: "#2E9E6E",
    tint: "#EAF7F0",
  },
  {
    number: "Volume IV",
    title: "The Clinical Reasoning Matrix",
    intro:
      "Where everything comes together. Includes the Sensory Decision Matrix™ so you stop treating every observation as isolated.",
    listLabel: "You'll learn how to organize:",
    items: [
      "Observation",
      "Context",
      "Sensory system to investigate",
      "Possible pattern",
      "Missing information",
      "Working hypothesis",
    ],
    goal: "See the relationships between observations instead of isolated pieces of information.",
    color: "#E07A3F",
    tint: "#FDF1E8",
  },
  {
    number: "Volume V",
    title: "From Working Hypothesis to Functional Goals",
    intro: "Once your observations are organized, answer the next question: “What am I actually trying to change?”",
    listLabel: "Frameworks for:",
    items: [
      "Functional goal setting",
      "Observable outcomes",
      "Meaningful activities",
      "Baseline information",
      "Progress tracking",
      "Clinical documentation",
      "Communicating progress",
    ],
    goal: "No vague “Improve sensory processing.” Think Function → Context → Observable Change → Measurement.",
    color: "#D9A21B",
    tint: "#FDF7E4",
  },
  {
    number: "Volume VI",
    title: "The Complete Sensory Decision Workflow",
    intro:
      "Put the entire system together in an 8-step pathway — and learn to recognize when you DON'T have enough information to conclude.",
    listLabel: "The complete pathway:",
    items: [
      "Define the functional concern",
      "Organize relevant history",
      "Identify meaningful observations",
      "Map sensory systems to investigate",
      "Identify missing information",
      "Build a working hypothesis",
      "Translate into functional goals",
      "Track and communicate outcomes",
    ],
    goal: "Know what the evidence currently supports — and what still needs to be investigated.",
    color: "#7A5BD6",
    tint: "#F2EEFC",
  },
];

export type Tool = { icon: LucideIcon; title: string; text: string };

export const tools: Tool[] = [
  { icon: BrainIcon, title: "Sensory Decision Trees", text: "Visual pathways from an observation toward the next question to investigate." },
  { icon: ClipboardListIcon, title: "Clinical Observation Checklists", text: "Structured sheets to organize what you're seeing during sessions." },
  { icon: SearchIcon, title: "Missing Information Filters", text: "Prompts to identify gaps before forming a stronger hypothesis." },
  { icon: Table2Icon, title: "Sensory Reasoning Matrices", text: "Tables connecting observations, context, systems and functional impact." },
  { icon: TargetIcon, title: "Functional Goal Templates", text: "Frameworks for turning broad concerns into clear, observable goals." },
  { icon: FileTextIcon, title: "Case Organization Sheets", text: "Printable pages for organizing a case from beginning to end." },
  { icon: TrendingUpIcon, title: "Progress Tracking Templates", text: "Simple tools for documenting change and communicating progress." },
];

export type Bonus = {
  number: number;
  title: string;
  value: number;
  description: string;
  items: string[];
  closing: string;
  image: string;
};

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: "The Intelligent Intake Guide™",
    value: 37,
    description:
      "A structured intake guide that helps you identify the questions worth asking — and understand why each area of information may matter to your sensory reasoning.",
    items: ["Intake questions", "Developmental history prompts", "Functional questions", "Routine questions", "School questions", "Family concerns", "Context questions", "“Why does this matter?” explanations"],
    closing: "You're not simply collecting information. You're learning how to organize relevant information.",
    image: "https://cdn.magicpatterns.com/uploads/51pv8z2kDFVBAE7WZ577Pp/BONUS_1_SENSORY-EN.png",
  },
  {
    number: 2,
    title: "The 15-Minute Case Organizer™",
    value: 37,
    description:
      "A rapid case-organization worksheet: Concern → Observation → Context → System → Missing Information → Working Hypothesis.",
    items: ["Step-by-step case organization", "Case overview template", "Observation to hypothesis matrix", "Missing information filter"],
    closing: "Turn a pile of notes into a structured case overview in approximately 15 minutes.",
    image: "https://cdn.magicpatterns.com/uploads/1BBUfBFt6sju18Bjeuwt63/BONUS_2_SENSORY-EN.png",
  },
  {
    number: 3,
    title: "The Sensory Clinical Glossary™",
    value: 27,
    description: "A quick-reference glossary of key terminology you can keep next to your workspace.",
    items: ["Sensory integration", "Modulation", "Discrimination", "Praxis", "Sensory systems", "Functional performance", "Clinical reasoning"],
    closing: "Quick reference whenever a term slips your mind.",
    image: "https://cdn.magicpatterns.com/uploads/b8ewBWThiL9N3z29mExobM/BONUS_3_SENSORY-EN.png",
  },
  {
    number: 4,
    title: "10 Case Reasoning Challenges™",
    value: 29,
    description: "Ten educational mini-cases to practice the Sensory Decision Map™. For each case you'll work through:",
    items: ["What do you notice?", "What information is missing?", "What would you investigate?", "What hypotheses could be considered?", "What functional impact should you explore?"],
    closing: "The purpose isn't to memorize answers. It's to train the process of thinking.",
    image: "https://cdn.magicpatterns.com/uploads/dDyhzHTLgZAf7a91guMatt/BONUS_4_SENSORY-EN.png",
  },
  {
    number: 5,
    title: "The Clinical Progress & Goal Tracker™",
    value: 17,
    description: "A practical collection of printable tools for:",
    items: ["Baseline documentation", "Goal setting", "Progress tracking", "Session observations", "Functional outcomes", "Parent communication", "Visual progress summaries"],
    closing: "Know whether the intervention is actually making a functional difference.",
    image: "https://cdn.magicpatterns.com/uploads/pmMEcnZfqJSgWSVjjPddCz/BONUS_5_SENSORY-EN.png",
  },
];

export type Testimonial = { name: string; role: string; photo: string; title: string; quote: string };

export const testimonials: Testimonial[] = [
  {
    name: "Sarah M.",
    role: "Pediatric Occupational Therapist",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/68215ac7-e3fe-42fa-bbec-083fb7d836ca.jpg",
    title: "I finally have a way to organize what I'm seeing.",
    quote:
      "I had studied sensory integration before, but my biggest difficulty was connecting everything when I was looking at an actual case. The visual structure made that process much easier for me.",
  },
  {
    name: "Danielle R.",
    role: "Occupational Therapist",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/56324891-3816-4f13-b091-97a1525a1bed.jpg",
    title: "It feels completely different from another course.",
    quote:
      "What I liked most is that I don't have to sit through hours of lessons. I can open the map, find the section I need and use it as a reasoning reference.",
  },
  {
    name: "Michael T.",
    role: "Occupational Therapist",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/fc8e91f6-7c08-4adf-8438-1b0eac5f11cd.jpg",
    title: "The missing-information section was a game changer.",
    quote:
      "The questions helped me realize that sometimes I was trying to reach a conclusion before I had collected enough information.",
  },
  {
    name: "Lucía G.",
    role: "Occupational Therapist",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/bfac2749-ef5e-4fe8-9d4f-5f4a1b213274.jpg",
    title: "I keep the checklists next to my workspace.",
    quote: "The practical tools are what made the biggest difference for me. I can actually use them while organizing my cases.",
  },
  {
    name: "Grace L.",
    role: "Occupational Therapist",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/35956341-5c07-4312-b8d4-404117e653c6.jpg",
    title: "It helped me stop treating every observation separately.",
    quote:
      "The matrix helped me see how the different pieces of information connect instead of looking at every behavior as an isolated problem.",
  },
];

export type Faq = { q: string; a: string };

export const faqs: Faq[] = [
  {
    q: "How will I receive the material?",
    a: "Immediately after your purchase, you'll receive instructions to access and download the Sensory Decision Map™ volumes and all included bonuses in digital format.",
  },
  {
    q: "Is this a course?",
    a: "No — that's one of the biggest differences. The Sensory Decision Map™ is a digital clinical reasoning system made of six premium volumes, visual maps, worksheets, checklists, matrices and practical tools. Study it from beginning to end or jump directly to the section you need.",
  },
  {
    q: "Who is this for?",
    a: "The system is designed primarily for Occupational Therapists and Occupational Therapy students interested in sensory integration and pediatric clinical reasoning.",
  },
  {
    q: "Do I need previous experience with Sensory Integration?",
    a: "Not necessarily. The foundational maps help you organize your understanding, while the system also serves as a practical reference for experienced professionals.",
  },
  {
    q: "Does this replace standardized assessments?",
    a: "No. The Sensory Decision Map™ does not provide training for administering standardized tests and should not be used as a substitute for validated assessment procedures, professional training, clinical judgment or appropriate evaluation. It is designed to help you organize observations and clinical reasoning.",
  },
  {
    q: "Can I print the materials?",
    a: "Yes. The worksheets, checklists, matrices and practical resources work digitally and can also be printed for personal professional use.",
  },
  {
    q: "Is this a one-time payment?",
    a: "Yes. You pay USD $27 once. There is no monthly subscription.",
  },
  {
    q: "How long will I have access?",
    a: "Lifetime digital access to the purchased materials, subject to the applicable product terms.",
  },
  {
    q: "What if I change my mind?",
    a: "You have a 7-day guarantee. Review the materials and decide whether the system is right for you. If it isn't, request your refund according to the applicable guarantee terms.",
  },
];

export const finalFlow = [
  "What you see",
  "What it could mean",
  "What you still need to know",
  "What you want to investigate",
  "What functional change you want to measure",
];

export type Buyer = { name: string; place: string };

export const buyers: Buyer[] = [
  { name: "Valentina R.", place: "Bogotá, Colombia" },
  { name: "Mariana G.", place: "Guadalajara, Mexico" },
  { name: "Camila S.", place: "Buenos Aires, Argentina" },
  { name: "Lucía M.", place: "Madrid, Spain" },
  { name: "Daniela P.", place: "Santiago, Chile" },
  { name: "Sofía H.", place: "Lima, Peru" },
  { name: "Andrea V.", place: "Quito, Ecuador" },
  { name: "Gabriela T.", place: "San José, Costa Rica" },
  { name: "Paula F.", place: "Montevideo, Uruguay" },
  { name: "Carolina D.", place: "Santo Domingo, Dominican Republic" },
  { name: "Fernanda L.", place: "Monterrey, Mexico" },
  { name: "Isabella C.", place: "San Juan, Puerto Rico" },
  { name: "Natalia B.", place: "Asunción, Paraguay" },
  { name: "Alejandra Q.", place: "Ciudad de Panamá, Panama" },
  { name: "María José A.", place: "Guatemala City, Guatemala" },
  { name: "Ximena O.", place: "La Paz, Bolivia" },
];
